import { HTTP_INTERCEPTORS, provideHttpClient } from '@angular/common/http';
import {
	EnvironmentProviders,
	ErrorHandler,
	importProvidersFrom,
	makeEnvironmentProviders
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
	MissingTranslationHandler,
	TranslateLoader,
	provideTranslateService
} from '@ngx-translate/core';
import {
	TRANSLATE_HTTP_LOADER_CONFIG,
	TranslateHttpLoader
} from '@ngx-translate/http-loader';

// Loading bar modules
import { LOADING_BAR_CONFIG, LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';

// Import services that need to be provided
import { CountriesService } from './countries/countries.service';
import { EventService } from './events.service';
import { IdentityService } from './identity/identity.service';
import { AnalyticsInsightsService } from './logger/analytics/analytics-insights.service';
import { ErrorHandlerService } from './logger/errorhandler.service';
import { LoggingService } from './logger/logging.service';
import { MenuService } from './menu/menu.service';
import { SettingsService } from './settings/settings.service';
import {
	MyMissingTranslationHandler,
	TranslatorService
} from './translator/translator.service';

// Import interceptors (using class-based approach for Angular 19)
import { AuthInterceptor } from 'angular-auth-oidc-client';
import { SignalRInterceptor } from './interceptors/signalr/signalr.interceptor';
import { GlobalInterceptor } from './logger/global.interceptor';

// Import auth config
import { AuthConfigModule } from '../auth/auth-config.module';

// Factory function for TranslateLoader
export function createTranslateLoader() {
	return new TranslateHttpLoader();
}

/**
 * Provides all core application services and configurations
 * This replaces the CoreModule for standalone applications
 */
export function provideCore(): EnvironmentProviders {
	return makeEnvironmentProviders([
		// HTTP Client (interceptors will be provided separately for compatibility)
		provideHttpClient(),

		// HTTP Interceptors (using legacy token approach for existing interceptors)
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: GlobalInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: SignalRInterceptor,
			multi: true
		},

		// Animations
		provideAnimations(),

		{
			provide: TRANSLATE_HTTP_LOADER_CONFIG,
			useValue: {
				resources: [{ prefix: './assets/i18n/', suffix: '.json' }]
			}
		},

		// Translation service (ngx-translate v18 standalone provider API)
		provideTranslateService({
			missingTranslationHandler: {
				provide: MissingTranslationHandler,
				useClass: MyMissingTranslationHandler
			},
			loader: {
				provide: TranslateLoader,
				useFactory: createTranslateLoader
			}
		}),

		// Loading bar modules
		importProvidersFrom(
			LoadingBarModule,
			LoadingBarHttpClientModule,
			LoadingBarRouterModule
		),

		// Loading bar configuration
		{
			provide: LOADING_BAR_CONFIG,
			useValue: { latencyThreshold: 10 }
		},

		// Auth configuration (still using module import)
		importProvidersFrom(AuthConfigModule),

		// Core services
		SettingsService,
		TranslatorService,
		CountriesService,
		EventService,
		LoggingService,
		AnalyticsInsightsService,
		IdentityService,
		MenuService,

		// Error handler
		{
			provide: ErrorHandler,
			useClass: ErrorHandlerService
		}
	]);
}
